<script setup>
    import __ from '@components/../mixins/locale'
    import axios from 'axios'
    import Gallery from '@components/Gallery.vue'
    import InputField from '@components/InputField.vue'
    import { onMounted, reactive, ref, watch } from 'vue'

    const props = defineProps({
        user: {
            type: Object,
            required: true
        },
        language: {
            type: String,
            required: true
        },
        editingId: {
            type: Number,
            required: false,
            default () {
                return null
            }
        },
        formOptions: {
            type: Object,
            required: true
        },
        apiToken: {
            type: String,
            required: true
        }
    });

    const emit = defineEmits([
        'advert-status',
        'editingIdRemoved',
        'advert-form-loaded'
    ]);

    const form = reactive({
        data: {
            type: null,
            title: '',
            content: '',
            price: [
                {
                    service: null, 
                    price: null
                }
            ],
            currency: 34,
            expires_in: null,
            location: null,
            photos: []
        }
    });

    const errors = ref(null);

    const loading = ref(false);

    const addAnotherPrice = () => {
        form.data.price.push({
            service: null, 
            price: null
        });
    }

    const getAdvert = (id) => {
        loading.value = true;
        errors.value = null;
        axios.get('/api/advert/' + id + '?api_token=' + props.apiToken)
            .then(response => {
            if (response.data) {
                let responseData = response.data;
                
                form.data = responseData;
                // form.data.price = JSON.parse(form.data.price);
                // form.data.currency = 34;
                emit('advert-status', form.data.published_at ? 3 : 2);
                loading.value = false;
            }
        });
    }

    const saveAdvert = (publish=false) => {
        loading.value = true;
        errors.value = null;
        form.data.user_id = props.user.user_id;

        if (props.editingId) {
            form.data.editing_id = props.editingId;
        }

        if (publish) {
            form.data.publish = 1;
        }
        
        axios.post('/api/advert?api_token=' + props.apiToken, form.data).then(response => {
            window.location.reload();
        }).catch(caughtErrors => {
            loading.value = false;
            errors.value = caughtErrors.response.data.errors;
        });
    }

    const clearEdit = () => {
        emit('editingIdRemoved', null);
        
        errors.value = null;
        
        form.data = {
            type: null,
            title: '',
            content: '',
            price: [
                {
                    service: null, 
                    price: null
                }
            ],
            currency: 34,
            expires_in: null,
            location: null,
            photos: [],
            language: props.language
        };
    }

    const typeChanged = () => {
        form.data.price = [{
            service: null, 
            price: null
        }];
    }

    onMounted(() => {
        form.data.language = props.language;
        form.data.location = props.user.location;
        form.data.formattedAddress = props.user.formattedAddress;
    });

    watch(() => props.editingId, (val) => {
        if (val != null) {
            getAdvert(val);
        } else {
            clearEdit();
        }
    });

    watch(loading, (val) => {
        emit('advert-form-loaded', ((val === true) ? false : true));
    });
</script>

<template>
    <div>
        <div v-if="loading">
            <div class="loading-indicator"><div></div><div></div><div></div></div>
        </div>
        <template v-else>
            <p v-if="form.data.published_at && !form.data.isExpired">
                {{ __('Your published advert expires') }}: {{ form.data.expires_at }}
            </p>

            <div class="row">
                <InputField input-type="radio" :inline="1" name="type" :label="__('Type')" :errors="errors" :placeholder="__('Type')" @update:field="form.data.type = $event; typeChanged()" :data="form.data.type" :options="formOptions.type" />
            </div>
            
            <div class="row">
                <InputField input-type="text" name="title" :label="__('Title')" :errors="errors" :placeholder="__('Title')" @update:field="form.data.title = $event" :data="form.data.title" />
            </div>
            
            <div class="row">
                <InputField input-type="textarea" name="content" :label="__('Content')" :errors="errors" :placeholder="__('Content')" @update:field="form.data.content = $event" :data="form.data.content" />
            </div>
            
            <template v-if="form.data.type > 1">
                <div class="row">
                    <InputField input-type="select" name="currency" :label="__('Currency')" :errors="errors" :placeholder="__('Currency')" @update:field="form.data.currency = $event" :data="form.data.currency" :options="formOptions.currency" />
                </div>

                <label v-if="form.data.type === 2">{{ __('Prices') }}</label>
                <label v-if="form.data.type === 3">{{ __('Price') }}</label>
                <div class="row" v-for="(price, index) in form.data.price">
                    <InputField v-if="form.data.type === 2" input-type="text" name="price" :disable-label="true" :label="__('Service')" :errors="errors" :placeholder="__('Service')" @update:field="form.data.price[index].service = $event" :data="form.data.price[index].service" />
                    <InputField input-type="money" name="price" :disable-label="true" :label="__('Price')" :errors="errors" :placeholder="__('Price')" @update:field="form.data.price[index].price = $event" :data="form.data.price[index].price" />
                </div>
                <button v-if="form.data.type === 2" class="btn btn-primary" style="display: block; margin-bottom: 10px" @click="addAnotherPrice()">+</button>
            </template>

            <div class="row" v-if="!form.data.published_at || form.data.published_at == null || form.data.isExpired">
                <InputField input-type="radio" :inline="1" name="expires_in" :label="__('Expires')" :errors="errors" :placeholder="__('Expires')" @update:field="form.data.expires_in = $event" :data="form.data.expires_in" :options="formOptions.expires_in" />
            </div>

            <div class="row">
                <InputField input-type="location" name="location" :label="__('Location')" :errors="errors" :placeholder="form.data.formattedAddress ? form.data.formattedAddress : __('Where do you live?')" @update:field="form.data.location = $event" :data="form.data.location" />
            </div>

            <Gallery ref="gallery" :uploader="true" :excludeFromGallery="true" :items="form.data.photos" :user="user" :type="2" :form-errors="errors" @uploaded="form.data.photos = $event"></Gallery>
            
            <div class="float-right" style="margin-bottom: 20px">
                <button v-if="!form.data.published_at || form.data.published_at == null" class="btn btn-secondary" @click="saveAdvert()">{{ __('Save to Drafts') }}</button>
                <button class="btn btn-success" @click="saveAdvert(true)">
                    <template v-if="form.data.published_at">
                        <template v-if="form.data.isExpired">{{ __('Renew') }}</template>
                        <template v-else>{{ __('Save') }}</template>
                    </template>
                    <template v-else>{{ __('Publish') }}</template>
                </button>
            </div>
        </template>
    </div>
</template>